import { useCampaignData, useJobillaOrgId } from '@/hooks'
import { CandidateCard, LinkLocalProps } from '@/ui'

import { useReportCardView } from '@/ui/utilities/hooks/use-report-card-view'
import { createFileRoute, Link, useMatches, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'

import { z } from 'zod'
import { useCandidates } from '../-useCandidates'

const candidateSearchSchema = z.object({
	tab: z.enum(['responses', 'profile', 'emails', 'whatsapp', 'interviews', 'meeting']).optional(),
})

export const Route = createFileRoute(
	'/_auth/company/$companyId/$campaignId/candidates/$candidateId',
)({
	validateSearch: candidateSearchSchema,
	component: RouteComponent,
})

function RouteComponent() {
	const params = Route.useParams()
	const navigate = useNavigate()
	const matches = useMatches()
	const organisationId = useJobillaOrgId()

	// Detect if email child route is active
	const emailMatch = matches.find((match) =>
		match.routeId === '/_auth/company/$companyId/$campaignId/candidates/$candidateId/emails/$emailId'
	)
	const selectedEmailId = (emailMatch?.params as { emailId?: string })?.emailId ?? null

	const getEmailUrl = useCallback((emailId: string) => {
		const path =
			`/company/${params.companyId}/${params.campaignId}/candidates/${params.candidateId}/emails/${emailId}`
		return `${window.location.origin}${path}`
	}, [params])

	const {
		stages,
		candidates,
		selectedCandidate,
		handleCandidateMove,
	} = useCandidates(
		params.campaignId,
		params.candidateId,
	)

	useReportCardView(params.candidateId)

	// Calculate navigation state
	const { currentIndex, hasPrevious, hasNext } = useMemo(() => {
		const index = candidates.findIndex((c) => c.id === params.candidateId)
		return {
			currentIndex: index,
			hasPrevious: index > 0,
			hasNext: index >= 0 && index < candidates.length - 1,
		}
	}, [candidates, params.candidateId])

	const navigateToCandidate = (candidateId: string) => {
		navigate({
			to: '/company/$companyId/$campaignId/candidates/$candidateId',
			params: {
				companyId: params.companyId,
				campaignId: params.campaignId,
				candidateId,
			},
		})
	}

	const handlePreviousCandidate = () => {
		if (hasPrevious) {
			navigateToCandidate(candidates[currentIndex - 1].id)
		}
	}

	const handleNextCandidate = () => {
		if (hasNext) {
			navigateToCandidate(candidates[currentIndex + 1].id)
		}
	}

	// This is the parsed campaign type.
	const { campaign: transformedCampaign, formQuestions } = useCampaignData({ campaignId: params.campaignId })

	if (!selectedCandidate) {
		return <div></div>
	}

	const candidatesLinkRenderer = (campaignId: string, props: LinkLocalProps) => (
		<Link
			to="/company/$companyId/$campaignId/candidates"
			params={{ companyId: params.companyId, campaignId }}
			{...props}
		/>
	)

	return (
		<CandidateCard
			selectedCandidate={selectedCandidate}
			campaign={transformedCampaign}
			organisationId={organisationId}
			candidatesLinkRenderer={candidatesLinkRenderer}
			stages={stages}
			formQuestions={formQuestions}
			onStageChange={(candidateId, stageId) => handleCandidateMove([candidateId], stageId)}
			onPreviousCandidate={handlePreviousCandidate}
			onNextCandidate={handleNextCandidate}
			hasPreviousCandidate={hasPrevious}
			hasNextCandidate={hasNext}
			selectedEmailId={selectedEmailId}
			getEmailUrl={getEmailUrl}
		/>
	)
}
